import { HttpsUrl } from "../https-url";
import {
  CarrierDefinition,
  TrackingProviderConfig,
} from "./carrier-definition";
import { applyCarrierMappingRules } from "./carrier-mapping";
import {
  type CarrierMapping,
  CarrierMappingMatchType,
} from "./carrier-mapping-types";
import {
  CanonicalCarrierCode,
  carrierRegistry,
  getCanonicalCarrierCode,
} from "./carrier-registry";
import { inferCarrierFromTrackingCodeThirdPartyLibrary } from "./tracking-number-fallback";
import { TrackingProvider } from "./tracking-provider";

const ECN_SUFFIX = "_ecn";

// cSpell:disable

/**
 * Resolve a carrier code to its definition from the registry.
 *
 * Resolution flow:
 * 1. Normalize carrier string (lowercase, trim, spaces/hyphens to underscores)
 * 2. Evaluate global carrier rules (corrections, aliases, tracking code inference)
 * 3. Handle _ecn suffix convention
 * 4. Look up in registry
 *
 * When trackingCode is provided:
 * - Correction rules can override a known carrier based on tracking code evidence
 * - Inference rules can identify the carrier when no carrier is provided
 */
export function resolveCarrier(
  carrier: string | undefined,
  trackingCode?: string,
): CarrierResolution {
  const normalized = carrier ? normalizeCarrierString(carrier) : undefined;

  if (normalized && trackingCode && PLACEHOLDER_CARRIERS.has(normalized)) {
    const inferred = resolveCarrier(undefined, trackingCode);
    if (inferred.found) {
      return inferred;
    }
  }

  const mapped = applyCarrierMappingRules({
    carrier: normalized,
    trackingCode,
    rules: GLOBAL_CARRIER_RULES,
  });

  if (mapped) {
    const result = lookupInRegistry(
      mapped,
      CarrierResolutionSource.GLOBAL_RULES,
    );
    if (result.found) {
      return result;
    }
  }

  // Second pass: if carrier was provided but unresolvable, try with carrier=undefined
  // so UNSPECIFIED tracking code inference rules can fire as fallback.
  if (normalized && trackingCode) {
    const inferred = applyCarrierMappingRules({
      carrier: undefined,
      trackingCode,
      rules: GLOBAL_CARRIER_RULES,
    });
    if (inferred) {
      const result = lookupInRegistry(
        inferred,
        CarrierResolutionSource.TRACKING_CODE_INFERENCE,
      );
      if (result.found) {
        return result;
      }
    }
  }

  if (trackingCode) {
    const librarySlug =
      inferCarrierFromTrackingCodeThirdPartyLibrary(trackingCode);
    if (librarySlug) {
      const result = lookupInRegistry(
        librarySlug,
        CarrierResolutionSource.TRACKING_NUMBER_LIBRARY,
      );
      if (result.found) {
        return result;
      }
    }
  }

  return { found: false, originalCarrier: carrier };
}

/**
 * Get the human-readable display name for a carrier code.
 * Returns the registry display name if found, otherwise returns the original carrier string.
 */
export function getCarrierDisplayName(carrier: string | undefined): string {
  if (!carrier) {
    return "";
  }
  const resolution = resolveCarrier(carrier);
  return resolution.found ? resolution.definition.displayName : carrier;
}

/**
 * Get the carrier's native tracking page URL for a given tracking code.
 * Returns undefined if the carrier is unknown or has no URL template.
 */
export function getCarrierTrackingUrl(
  carrier: string | undefined,
  trackingCode: string | undefined,
): HttpsUrl | undefined {
  if (!carrier || !trackingCode) {
    return undefined;
  }

  const resolution = resolveCarrier(carrier);
  if (!resolution.found || !resolution.definition.trackingUrl) {
    return undefined;
  }

  return resolution.definition.trackingUrl(trackingCode);
}

/** Names that are not carriers: the tracking code wins, the alias is only the default. */
const PLACEHOLDER_CARRIERS: ReadonlySet<string> = new Set(["shopify"]);

// in general, keep the most specific (rarely used) rules at the top
// and the most general, broadly-applying rules at the bottom.
const GLOBAL_CARRIER_RULES: readonly CarrierMapping[] = [
  // ── Section 1: Carrier corrections ──
  // When the provided carrier + tracking code together indicate the carrier is wrong.
  {
    value: "tele_post",
    matchType: CarrierMappingMatchType.EXACT,
    targetCarrier: "cirro_ecommerce",
    trackingCode: {
      value: "^CR\\d{12}$",
      matchType: CarrierMappingMatchType.REGEX,
    },
  },
  // GFUS codes are GOFO's, but Shopify usually labels them Tele Post (or USPS, 4PX, ...),
  // none of which return scans. CIRRO and YunExpress do return scans for them, so leave those.
  {
    value: "^(?!cirro|yun_?express|yuntu)",
    matchType: CarrierMappingMatchType.REGEX,
    targetCarrier: "gofo_express",
    trackingCode: {
      value: "^GFUS\\d{14}$",
      matchType: CarrierMappingMatchType.REGEX,
    },
  },

  // ── Section 2: Name aliases ──
  // Map common carrier name variations to canonical registry slugs.
  alias("australiapost", "australia_post"),
  alias("australia_post_mypost_business", "australia_post"),
  alias("australia_post_(standard_parcel)", "australia_post"),
  alias("benben", "ben_ben"),
  alias("apg", "apg_ecommerce"),
  alias("apc", "apc_postal_logistics"),
  alias("apc_postal", "apc_postal_logistics"),
  alias("dhlecs", "dhl_ecommerce"),
  alias("dhl_ecs", "dhl_ecommerce"),
  alias("dhlglobalmail", "dhl_ecommerce"),
  alias("dhlglobalmailinternational", "dhl_ecommerce"),
  alias("dhlgm", "dhl_ecommerce"),
  // ── OSM Worldwide aliases ──
  // TODO: The OSM→USPS aliases predate the OSM Worldwide registry entry and
  // the Track123 integration. Now that OSM can be tracked natively via
  // Track123, merchants may legitimately want OSM shipments routed through OSM
  // instead of collapsed to USPS. Consider removing, but doing so changes
  // tracker routing for every merchant currently relying on the alias, so
  // verify per-merchant first. Would also cost us more money to track via Track123.
  // Merchant-level mappings (e.g. `osm → osm_worldwide_ecn`) are the per-merchant escape hatch.
  alias("osm", "usps"),
  alias("osmworldwide", "usps"),
  alias("osm_worldwide", "usps"),
  alias("osm_worldwideparcel_select", "usps"),
  // Not a USPS collapse.
  // No idea why this exists. Was someone sending us raw Easypost data??
  alias("osmworldwidev2", "osm_worldwide"),
  alias("passportinternational", "passport"),
  alias("tusk_logistics", "tusk"),
  alias("upsmi", "ups_mail_innovations"),
  alias("ups_ground", "ups"),
  alias("ups_surepost", "ups"),
  alias("ups_ground_saver", "ups"),
  alias("returnmates", "sway"),
  alias("cirrotrack", "cirro_ecommerce"),
  alias("ontrack", "on_trac"),
  alias("usps+vip", "usps"),
  alias("shopify", "usps"),
  alias("collective", "usps"),
  alias("ontrac", "on_trac"),
  alias("ontrac_v3", "on_trac"),
  alias("passportship", "passport"),
  alias("gofo", "gofo_express"),
  alias("4px", "four_px"),
  alias("amazon_logistics", "amazon"),
  alias("amazon_logistics_uk", "amazon_uk"),
  alias("amazon_logistics_us", "amazon"),
  alias("amazon_shipping_+_amazon_mcf", "amazon"),
  alias("amazon_shipping_amazon_mcf", "amazon"),
  alias("amzn", "amazon"),
  alias("amazon_fulfillment", "amazon"),
  alias("apc_lax", "apc_postal_logistics"),
  alias("apc_nj", "apc_postal_logistics"),
  alias("apc_ord", "apc_postal_logistics"),
  alias("arcbest_panther", "arc_best"),
  alias("aramex_australia", "aramex_au"),
  alias("asendia", "asendia_usa"),
  alias("asendiausa", "asendia_usa"),
  alias("belgium_post", "bpost"),
  alias("bettertrucks", "better_trucks"),
  alias("canada_post_expedited_parcel_piece_rated", "canada_post"),
  alias("canada_post_one_balance", "canada_post"),
  alias("canadapost", "canada_post"),
  alias("canpar_courier", "canpar"),
  alias("canpar_express", "canpar"),
  alias("cdl_last_mile", "cdl"),
  alias("laser_ship", "lasership"),
  alias("chinapost", "china_post"),
  alias("cirro", "cirro_ecommerce"),
  alias("cirro_e_commerce", "cirro_ecommerce"),
  alias("cirroparcel", "cirro_parcel"),
  alias("city_mail", "city_mail_se"),
  alias("citymail_pk", "city_mail_pk"),
  alias("citymail_se", "city_mail_se"),
  alias("colis_priv", "colis_prive"),
  alias("columbuslastmile", "cdl"),
  alias("courierexpress", "courier_express"),
  alias("couriers_please", "couriersplease"),
  alias("daipost", "dai_post"),
  alias("deutsche_post_mail", "deutsche_post"),
  alias("deutschepostuk", "deutsche_post_uk"),
  alias("dhl", "dhl_ecommerce"),
  alias("dhl_ecommerce_us", "dhl_ecommerce"),
  alias("dhl_global_mail", "dhl_ecommerce"),
  alias("dhl_ecommerce_plt_ssi", "dhl_ecommerce"),
  alias("dhlexpress", "dhl_express"),
  alias("dhlpaket", "dhl_paket"),
  alias("dhlparcel", "dhl_parcel"),
  // 3PLs relabel DoorDash with facility and service suffixes ("Doordash - Indy2
  // - RFIL", "DoorDash DashLink"); leave _ecn for lookupInRegistry
  {
    value: "^doordash_.*(?<!_ecn)$",
    matchType: CarrierMappingMatchType.REGEX,
    targetCarrier: "doordash",
  },
  alias("dashlink", "doordash"),
  alias("dpdnl", "dpd_nl"),
  alias("dpd(nl)", "dpd_nl"),
  alias("dpduk", "dpd_uk"),
  alias("estesexpress", "estes"),
  alias("federal_express", "fedex"),
  alias("fedex_2_day", "fedex"),
  alias("fedex_ground", "fedex"),
  alias("fedex_cb", "fedex_cross_border"),
  alias("fedexcrossborder", "fedex_cross_border"),
  alias("fedexsmartpost", "fedex_smart_post"),
  alias("fxsp", "fedex_smart_post"),
  alias("fastway_nz", "aramex_nz"),
  alias("fleetoptics", "fleet_optics"),
  alias("gls_us", "gls"),
  alias("gls_(us)", "gls"),
  alias("gls_ground", "gls"),
  alias("gso", "gls"),
  alias("hermes", "evri"),
  // Shopify sends Evri service levels ("Evri Standard POD"); leave _ecn for lookupInRegistry
  {
    value: "^evri_.*(?<!_ecn)$",
    matchType: CarrierMappingMatchType.REGEX,
    targetCarrier: "evri",
  },
  alias("ics_courier", "ics"),
  alias("intelcom_express", "intelcom"),
  alias("interlinkexpress", "interlink_express"),
  alias("japan_post_(en)", "japan_post"),
  alias("japan_post_(ja)", "japan_post"),
  alias("js_exp", "js_express"),
  alias("landmark", "landmark_global"),
  alias("landmarkglobal", "landmark_global"),
  alias("laposte", "la_poste"),
  alias("colissimo", "la_poste"),
  alias("loomisexpress", "loomis_express"),
  alias("new_zealand_post", "nz_post"),
  alias("ninjavan_int", "ninjavan"),
  alias("omniparcel", "omni_parcel"),
  alias("passport_global", "passport"),
  alias("passportglobal", "passport"),
  alias("passport_carrier", "passport"),
  alias("pitneybowes", "pitney_bowes"),
  alias("purolator_courier", "purolator"),
  alias("purolator_express", "purolator"),
  alias("purolator_ground", "purolator"),
  alias("purolatorexpress", "purolator"),
  alias("purolatorground", "purolator"),
  alias("qls", "quince_logistics"),
  alias("poczta_polska", "poland_post"),
  // PostNL domestic is not in the registry (requires postalCode at registration),
  // so route the bare "Post NL" / "postnl" carrier strings to PostNL International,
  // whose Track123 courier (postnl-parcels) does not require postalCode.
  alias("post_nl", "postnl_international"),
  alias("postnl", "postnl_international"),
  alias("r+l_carriers", "r_plus_l_carriers"),
  alias("rl_carriers", "r_plus_l_carriers"),
  alias("royalmail", "royal_mail"),
  alias("royalmailv3", "royal_mail_v3"),
  alias("rr_donnelley", "epost_global"),
  alias("rrdonnelley", "epost_global"),
  alias("saia_freight", "saia"),
  alias("second_closet", "gobolt"),
  alias("sfexpress", "sf_express"),
  alias("spee_dee_delivery_service", "speedee"),
  alias("shipbob", "ship_bob"),
  alias("telepost", "tele_post"),
  alias("tforce_final_mile", "tforce"),
  alias("tforceconcise", "tforce"),
  alias("toll_ipec", "toll"),
  alias("tusklogistics", "tusk"),
  alias("united_delivery_service", "uds"),
  alias("upsmailinnovations", "ups_mail_innovations"),
  alias("us_postal_service", "usps"),
  alias("usps_first_class_mail", "usps"),
  alias("usps_parcel_select", "usps"),
  alias("wanbexpress", "wanb_express"),
  alias("shipwizmo", "wizmo"),
  alias("xpologistics", "xpo"),
  alias("xpo_logistics", "xpo"),
  alias("yuntu", "yun_express"),
  alias("yunexpress", "yun_express"),

  // ── Section 3: Tracking code inference ──
  // Ordered from most specific to least specific.
  inference("^BEN\\d{8}BEN$", "ben_ben"),
  inference("^SY[A-Z]{2}\\d{9}$", "sunyou"),
  inference("^VEHO[A-Z]{3}\\d{9}$", "veho"),
  inference("^VH[A-Z0-9]{12}$", "veho"),
  inference("^TBA[0-9]{12}$", "amazon"),
  inference("^AQ\\d{9}CN$", "china_post"),
  inference("^[CD][0-9]{14}$", "on_trac"),
  // ACI Logistix: 26 digits containing "00000034"
  inference("^(?=.*00000034)\\d{26}$", "aci_logistix"),
  // GOFO Netherlands: GFNL + 13-14 digits
  inference("^GFNL\\d{13,14}$", "gofo_netherlands"),
  inference("^GFUS", "gofo_express"),
  inference("^LTN", "landmark_global"),
  inference("^EPG", "epost_global"),
  inference("^SWX", "swiftx"),
  inference("^SPX", "speedx"),
  inference("^SWAY", "sway"),
  inference("^CR\\d{12}$", "cirro_ecommerce"),
  inference("^CRA\\d{12}$", "cirro_ecommerce"),
  inference("^(UUS[0-9A-Z]{3}\\d{13}|UUSC\\d{12})$", "uniuni"),
  inference("^YT\\d{16}$", "yun_express"),
  inference("^WNB[A-Z]{2}\\d{10}YQ$", "wanb_express"),
  inference("^H\\d{2}[0-9A-Z]{2}[A-Z]\\d{10}$", "evri"),
  inference("^191\\d{2}A\\d{10}$", "apc_postal_logistics"),
  inference("^YW([A-Z]\\d{14}|[A-Z]{3}\\d{12})$", "yanwen"),
  inference("^6A\\d{11}$", "la_poste"),
  inference("^SDH\\d{10,11}$", "sdh"),
  inference("^1Z[A-Z0-9]{16}$", "ups"),
  inference("^[A-Z]{2}\\d{9}CA$", "canada_post"),
  inference("^[A-Z]{2}\\d{9}GB$", "royal_mail"),
  // Intelligent Mail Barcode (IMB): 20 digits starting with "0031"
  inference("^0031\\d{16}$", "pitney_bowes"),
  // USPS Intelligent Mail barcode: "420" + 5-digit zip prefix
  inference("^420\\d{5}", "usps"),
  // Asendia USA's S10 codes end in US like USPS's, but USPS never returns scans for them
  inference("^AS\\d{9}US$", "asendia_usa"),
  // USPS standard tracking numbers
  inference("^(9[2-5][0-9]{18,24}|[A-Z]{2}[0-9]{9}US)$", "usps"),
  // Last resort: bare 12-digit codes are often FedEx but ambiguous
  inference("^\\d{12}$", "fedex"),
];
// cSpell:enable

// Build slug index at module load time for O(1) lookups.
// Each carrier gets two keys: the canonical slug and the underscore-stripped form for fuzzier matching.
const aliasIndex = Object.fromEntries(
  Object.values(carrierRegistry).flatMap((d) => [
    [d.slug, d],
    [d.slug.replace(/_/g, ""), d],
  ]),
) as Record<string, CarrierDefinition>;

// Reverse index: (provider, normalized provider-specific carrierCode) -> CarrierDefinition.
//
// Used by provider response normalizers to map a carrier string echoed BACK by
// a provider (e.g. Track123's courierNameEN, EasyPost's tracker.carrier) to
// the canonical Redo carrier — WITHOUT running the input-side alias pipeline,
// which can incorrectly redirect a provider's own carrier code (e.g.
// alias("osmworldwide", "usps") would otherwise collapse Track123's OSM code
// to USPS).
//
// Each entry is registered under two keys: the normalized carrierCode and its
// underscore-stripped form, so freeform provider echoes like "OSM Worldwide"
// match the registry's "osmworldwide" code after normalization.
const providerCarrierCodeIndex: Partial<
  Record<TrackingProvider, Record<string, CarrierDefinition>>
> = (() => {
  const index: Partial<
    Record<TrackingProvider, Record<string, CarrierDefinition>>
  > = {};
  for (const definition of Object.values(carrierRegistry)) {
    for (const config of [
      definition.primaryProvider,
      definition.secondaryProvider,
    ]) {
      if (!config) {
        continue;
      }
      const normalized = normalizeCarrierString(config.carrierCode);
      const keys = [normalized, normalized.replace(/_/g, "")];
      const perProvider = (index[config.provider] ??= {});
      // First-wins on collision. A few legitimate ambiguities exist in the
      // registry today (e.g. EasyPost "Dpd" is shared between DPD and
      // DPD_LOCAL), so silently keeping the first registration avoids
      // crashing module load. Iteration order follows the registry's
      // declaration order, which is the closest we have to "preferred"
      // when two carriers really do share a provider code.
      for (const key of keys) {
        perProvider[key] ??= definition;
      }
    }
  }
  return index;
})();

/**
 * Look up the canonical carrier given a tracking provider and the carrier
 * code/label that provider returned. Performs ONLY a direct registry lookup —
 * does not apply input-side aliases, corrections, or tracking-code inference.
 *
 * Use this in provider response normalizers (Track123/EasyPost/FirstMile) when
 * you need to stamp `resolvedCarrier`. The input is a string that came out of
 * our own registry (via the provider's API), so we resolve it directly against
 * the registry rather than re-running the input pipeline that's designed for
 * external/unknown carrier strings.
 *
 * Returns undefined if the string doesn't match any registered provider
 * carrier code — callers can then fall back to `resolveCarrier` if they
 * actually have a freeform external carrier label (e.g. a webhook).
 */
export function lookupCarrierByProviderCode(
  provider: TrackingProvider,
  carrierCode: string | undefined,
): CarrierDefinition | undefined {
  if (!carrierCode) {
    return undefined;
  }
  const perProvider = providerCarrierCodeIndex[provider];
  if (!perProvider) {
    return undefined;
  }
  const normalized = normalizeCarrierString(carrierCode);
  return perProvider[normalized] ?? perProvider[normalized.replace(/_/g, "")];
}

/**
 * Get the provider-specific carrier code for an already-resolved Redo carrier.
 *
 * Inverse of {@link lookupCarrierByProviderCode}: given a canonical carrier
 * (e.g. `SupportedCarrier.FEDEX`) and the provider servicing it, return the
 * carrier code that provider's API expects (e.g. EasyPost's `"FedEx"`).
 *
 * Both the primary and secondary provider configs are checked, so a tracker
 * that registered via the fallback provider refreshes with that same provider's
 * code rather than the primary's.
 *
 * Returns undefined if the carrier is unknown or isn't serviced by `provider`.
 */
export function getProviderCarrierCode(
  carrier: string | undefined,
  provider: TrackingProvider,
): string | undefined {
  if (!carrier) {
    return undefined;
  }
  // carrierRegistry is keyed by SupportedCarrier (a string enum); widen to a
  // string index since the caller holds a loosely-typed resolved carrier.
  const definition = (carrierRegistry as Record<string, CarrierDefinition>)[
    carrier
  ];
  return definition ? providerCarrierCodeOf(definition, provider) : undefined;
}

// Like getProviderCarrierCode but for a raw carrier string (e.g. "Jitsu",
// "USPS"), resolving it to a registry carrier first.
export function getProviderCarrierCodeForRawCarrier(
  carrier: string | null | undefined,
  provider: TrackingProvider,
): string | undefined {
  if (!carrier) {
    return undefined;
  }
  const resolution = resolveCarrier(carrier);
  return resolution.found
    ? providerCarrierCodeOf(resolution.definition, provider)
    : undefined;
}

function providerCarrierCodeOf(
  definition: CarrierDefinition,
  provider: TrackingProvider,
): string | undefined {
  const configs: (TrackingProviderConfig | undefined)[] = [
    definition.primaryProvider,
    definition.secondaryProvider,
  ];
  return configs.find((config) => config?.provider === provider)?.carrierCode;
}

export enum CarrierResolutionSource {
  GLOBAL_RULES = "global_rules",
  TRACKING_CODE_INFERENCE = "tracking_code_inference",
  TRACKING_NUMBER_LIBRARY = "tracking_number_library",
}

export type CarrierResolution =
  | {
      found: true;
      definition: CarrierDefinition;
      matchedAlias: string;
      /** True if _ecn suffix was detected, indicating non-EasyPost provider should be preferred */
      preferNonEasyPost: boolean;
      canonicalCarrier: CanonicalCarrierCode;
      source: CarrierResolutionSource;
    }
  | { found: false; originalCarrier: string | undefined };

function normalizeCarrierString(carrier: string): string {
  return carrier
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_()+]/g, "");
}

function lookupInRegistry(
  carrier: string,
  source: CarrierResolutionSource,
): CarrierResolution {
  const hasEcnSuffix = carrier.endsWith(ECN_SUFFIX);
  const stripped = hasEcnSuffix
    ? carrier.slice(0, -ECN_SUFFIX.length)
    : carrier;

  const resolved =
    applyCarrierMappingRules({
      carrier: stripped,
      rules: GLOBAL_CARRIER_RULES,
    }) ?? stripped;

  const definition = aliasIndex[resolved];
  if (definition) {
    return {
      found: true,
      definition,
      matchedAlias: resolved,
      preferNonEasyPost: hasEcnSuffix,
      canonicalCarrier: getCanonicalCarrierCode(definition),
      source,
    };
  }

  return { found: false, originalCarrier: carrier };
}

/**
 * A common pattern of mapping where the raw carrier name is unambiguously
 * referring to a known carrier.
 */
function alias(value: string, targetCarrier: string): CarrierMapping {
  return { value, matchType: CarrierMappingMatchType.EXACT, targetCarrier };
}

/**
 * A common pattern of mapping where the carrier is unknown
 * but can be inferred from the tracking code.
 */
function inference(
  trackingCodePattern: string,
  targetCarrier: string,
): CarrierMapping {
  return {
    value: "",
    matchType: CarrierMappingMatchType.UNSPECIFIED,
    targetCarrier,
    trackingCode: {
      value: trackingCodePattern,
      matchType: CarrierMappingMatchType.REGEX,
    },
  };
}
